
import { useState } from 'react';
import { LevelConfig, Exercise } from '@/models/LearnTypes';
import { Lesson } from '@/models/Lesson';
import { generateExercisesForLevel } from '@/utils/exerciseUtils';
import { LEVEL_CONFIGS } from '@/constants/levelConfigs';

interface UseLevelManagementProps {
  setExercises: React.Dispatch<React.SetStateAction<Exercise[]>>;
  setTotalOriginalExercises: React.Dispatch<React.SetStateAction<number>>;
  setCurrentExerciseIndex: React.Dispatch<React.SetStateAction<number>>;
  setShowAnswer: React.Dispatch<React.SetStateAction<boolean>>;
  setUserInput: React.Dispatch<React.SetStateAction<string>>;
  setIsInputCorrect: React.Dispatch<React.SetStateAction<boolean | null>>;
  setWordsToReview: React.Dispatch<React.SetStateAction<string[]>>;
  selectedLesson: Lesson | null;
}

interface UseLevelManagementReturn {
  currentLevelIndex: number;
  levels: LevelConfig[];
  showLevelIntro: boolean;
  showLevelComplete: boolean;
  showLessonComplete: boolean;
  
  setCurrentLevelIndex: React.Dispatch<React.SetStateAction<number>>;
  setLevels: React.Dispatch<React.SetStateAction<LevelConfig[]>>;
  setShowLevelIntro: React.Dispatch<React.SetStateAction<boolean>>;
  setShowLevelComplete: React.Dispatch<React.SetStateAction<boolean>>;
  setShowLessonComplete: React.Dispatch<React.SetStateAction<boolean>>;
  
  startLevel: (levelIndex: number) => void;
  restartCurrentLevel: (shouldAwardXP?: boolean) => void;
  
  currentLevel: LevelConfig;
}

export const useLevelManagement = ({
  setExercises,
  setTotalOriginalExercises,
  setCurrentExerciseIndex,
  setShowAnswer,
  setUserInput,
  setIsInputCorrect,
  setWordsToReview,
  selectedLesson
}: UseLevelManagementProps): UseLevelManagementReturn => {
  const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
  const [levels, setLevels] = useState<LevelConfig[]>([...LEVEL_CONFIGS]);
  const [showLevelIntro, setShowLevelIntro] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [showLessonComplete, setShowLessonComplete] = useState(false);
  
  const currentLevel = levels[currentLevelIndex];
  
  const startLevel = (levelIndex: number) => {
    if (!selectedLesson) {
      console.error("No lesson selected when trying to start level");
      return;
    }
    
    console.log(`Starting level ${levelIndex + 1} for lesson: ${selectedLesson.name}`);
    
    // Reset mistake flags for the new level
    (window as any).__SPELLING_EXERCISE_HAD_MISTAKE__ = false;
    (window as any).__MATCH_EXERCISE_HAD_MISTAKE__ = false;
    (window as any).__PERFECT_LEVEL_STATUS__ = true; // Start as perfect
    
    // Also reset in localStorage
    try {
      // Clear global flags
      localStorage.setItem('spelling_exercise_had_mistake', 'false');
      localStorage.setItem('match_exercise_had_mistake', 'false');
      localStorage.setItem('perfect_level_status', 'true');
      
      // Clear level-specific spelling mistake flag for this level
      const levelSpecificKey = `spelling_exercise_had_mistake_level_${levelIndex}`;
      localStorage.setItem(levelSpecificKey, 'false');
      
      console.log(`🔄 [startLevel] Reset mistake flags for new level ${levelIndex + 1}`);
      console.log(`🔄 [startLevel] Cleared level-specific flag: ${levelSpecificKey}=false`);
    } catch (e) {
      console.error('Error resetting localStorage mistake flags:', e);
    }
    
    // Add a small delay to allow for animation transition
    setTimeout(() => {
      const newLevels = [...levels];
      newLevels[levelIndex].state = 'in-progress';
      setLevels(newLevels);
      setCurrentLevelIndex(levelIndex);
      
      const generatedExercises = generateExercisesForLevel(newLevels[levelIndex].type, selectedLesson.words);
      setExercises(generatedExercises);
      setTotalOriginalExercises(generatedExercises.length);
      
      setCurrentExerciseIndex(0);
      setShowAnswer(false);
      setUserInput('');
      setIsInputCorrect(null);
      setWordsToReview([]);
      
      setShowLevelIntro(false);
    }, 300); // Add a short delay for smoother transition
  };
  
  const restartCurrentLevel = (shouldAwardXP = false) => {
    if (!selectedLesson) return;
    
    // Reset mistake flags for the restarted level
    (window as any).__SPELLING_EXERCISE_HAD_MISTAKE__ = false;
    (window as any).__MATCH_EXERCISE_HAD_MISTAKE__ = false;
    (window as any).__PERFECT_LEVEL_STATUS__ = true; // Start as perfect
    
    // Also reset in localStorage
    try {
      // Clear global flags
      localStorage.setItem('spelling_exercise_had_mistake', 'false');
      localStorage.setItem('match_exercise_had_mistake', 'false');
      localStorage.setItem('perfect_level_status', 'true');
      
      // Clear level-specific spelling mistake flag for this level
      const levelSpecificKey = `spelling_exercise_had_mistake_level_${currentLevelIndex}`;
      localStorage.setItem(levelSpecificKey, 'false');
      
      console.log(`🔄 [restartCurrentLevel] Reset mistake flags for restarted level ${currentLevelIndex + 1}`);
      console.log(`🔄 [restartCurrentLevel] Cleared level-specific flag: ${levelSpecificKey}=false`);
    } catch (e) {
      console.error('Error resetting localStorage mistake flags:', e);
    }
    
    const currentLevel = levels[currentLevelIndex];
    const generatedExercises = generateExercisesForLevel(currentLevel.type, selectedLesson.words);
    setExercises(generatedExercises);
    setTotalOriginalExercises(generatedExercises.length);
    
    setCurrentExerciseIndex(0);
    setShowAnswer(false);
    setUserInput('');
    setIsInputCorrect(null);
    setWordsToReview([]);
    
    setShowLevelComplete(false);
    setShowLevelIntro(false);
  };
  
  return {
    currentLevelIndex,
    levels,
    showLevelIntro,
    showLevelComplete,
    showLessonComplete,
    
    setCurrentLevelIndex,
    setLevels,
    setShowLevelIntro,
    setShowLevelComplete,
    setShowLessonComplete,
    
    startLevel,
    restartCurrentLevel,
    
    currentLevel
  };
};
